
// src/AuthenticatedApp.jsx
import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useIsAuthenticated, useMsalAuthentication, useMsal } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { ApolloProvider } from '@apollo/client';
import { useSelector } from "react-redux";

import Layout from './components/Layout/Layout';
import { GraphQlClient } from './apolloClient';

import { Files } from './components/miscellaneous/Files';
import { ManageUsers } from './components/admin/ManageUsers';
import { ModerateFiles } from './components/admin/ModerateFiles';
import { ManageCompanies } from './components/admin/ManageCompanies';
import { AddCompany } from './components/AddCompany';
import { ManagerApproval } from './components/ManagerApproval';
import { CompanyAccessReport } from './components/Reports/CompanyAccessReport';
import { FileUploadReport } from './components/Reports/FileUploadReport';
import { LoginReport } from './components/Reports/LoginReport';
import { MainPage } from './components/miscellaneous/MainPage';
import { AccessRequest } from './components/AccessRequest';
import { ManageAccessRequests } from './components/admin/ManageAccessRequests';
import { ExternalUserRequest } from './components/ExternalUserRequest';
import { ManageExternalUserRequests } from './components/admin/ManageExternalUserRequests';
import { ManageNewCompanyRequests } from './components/admin/ManageNewCompanyRequests';
import { SplashPage } from './components/SplashPage/SplashPage';

const AuthenticatedApp = () => {
    const isAuthenticated = useIsAuthenticated();
    const { error } = useMsalAuthentication(InteractionType.Redirect); // SAFE: always called inside auth shell
    const { instance, accounts, inProgress } = useMsal();

    const [accessToken, setAccessToken] = useState();
    const currentUser = useSelector(state => state.auth);

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            instance.acquireTokenSilent({
                account: accounts[0],
                scopes: ["User.Read"]
            }).then(response => {
                if (response?.accessToken) {
                    setAccessToken(response.accessToken);
                }
            }).catch(() => { });
        }
    }, [inProgress, accounts, instance]);

    if (isAuthenticated) {
        return (currentUser.jwtIdToken && currentUser.jwtIdToken.idToken) ? (
            <ApolloProvider client={GraphQlClient(currentUser.jwtIdToken.idToken)}>
                <Layout>
                    <Routes>
                        <Route path="/splashpage" element={<SplashPage />} />
                        <Route path="/" element={<MainPage />} />
                        <Route path="/files" element={<Files />} />
                        <Route path="/manageusers" element={<ManageUsers />} />
                        <Route path="/moderatefiles" element={<ModerateFiles />} />
                        <Route path="/managecompanies" element={<ManageCompanies />} />
                        <Route path="/addcompany" element={<AddCompany />} />
                        {/* Authenticated users can still access this page in-shell */}
                        <Route path="/managerapproval" element={<ManagerApproval />} />
                         <Route exact path="/CompanyAccessReport" element={<CompanyAccessReport />} />
                         <Route exact path="/fileuploadreport" element={<FileUploadReport />} />
                         <Route exact path="/loginreport" element={<LoginReport />} />
                        <Route path="/accessrequest" element={<AccessRequest email={currentUser.user.email} />} />
                        <Route path="/manageaccessrequests" element={<ManageAccessRequests />} />
                        <Route path="/addexternaluser" element={<ExternalUserRequest />} />
                        <Route path="/manageexternaluserrequests" element={<ManageExternalUserRequests />} />
                        <Route path="/managenewcompanyrequests" element={<ManageNewCompanyRequests />} />
                    </Routes>
                </Layout>
            </ApolloProvider>
        ) : (
            <p>Loading. Please wait</p>
        );
    } else if (error) {
        return <span>An error occurred during login</span>;
    } else {
        return <span>Please wait. Redirecting to login.</span>;
    }
};

export default AuthenticatedApp;
